import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What is the most important factor in maintaining long-term financial wellness?",
    options: [
      "Consistent positive financial habits",
      "Winning the lottery",
      "Avoiding all financial decisions"
    ],
    correctAnswer: 0,
    explanation: "Consistent positive financial habits are the most important factor in maintaining long-term financial wellness. This includes regular saving, mindful spending, ongoing learning, and making intentional financial decisions that align with your values."
  },
  {
    question: "How does financial wellness connect to other aspects of holistic well-being?",
    options: [
      "Financial wellness is completely separate from other wellness areas",
      "Financial stress can impact mental health, while financial security enables better physical health and social connections",
      "Only physical health is affected by financial status"
    ],
    correctAnswer: 1,
    explanation: "Financial wellness is interconnected with other wellness domains - financial stress can cause anxiety and depression, while financial security reduces stress, enables access to healthcare, nutritious food, and social activities."
  },
  {
    question: "What is financial mindfulness?",
    options: [
      "Being aware of money only when bills are due",
      "Conscious awareness of spending patterns, financial goals, and the emotional relationship with money",
      "Ignoring financial details to avoid stress"
    ],
    correctAnswer: 1,
    explanation: "Financial mindfulness involves being consciously aware of your spending patterns, financial goals, emotional triggers around money, and how financial decisions align with your values and long-term well-being."
  },
  {
    question: "How can financial decisions be aligned with personal values?",
    options: [
      "By spending on anything that feels good in the moment",
      "By evaluating purchases and investments based on how they support your core values and life goals",
      "By following trends without personal reflection"
    ],
    correctAnswer: 1,
    explanation: "Aligning financial decisions with personal values involves reflecting on whether purchases, investments, and spending habits support your core values, life goals, and the kind of person you want to be."
  },
  {
    question: "What role does ongoing financial education play in long-term wellness?",
    options: [
      "No role - once you learn basics, you're done",
      "It helps adapt to changing economic conditions and make informed decisions throughout life",
      "It only benefits financial professionals"
    ],
    correctAnswer: 1,
    explanation: "Ongoing financial education is crucial for long-term wellness as it helps individuals adapt to changing economic conditions, understand new financial products, and make informed decisions that protect and grow their financial well-being over time."
  }
];

const FinancialModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('financialM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Holistic financial integration
              </li>
              <li>
                Long-term financial wellness habits
              </li>
              <li>
                Financial decision-making alignment
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your financial wellness journey by learning about maintaining financial wellness!</p>
            <p>Ready to explore habits for long-term financial success?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/financial-resources')}
          >
            Return to Financial Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Holistic Financial Integration</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about integrating financial wellness into your overall well-being.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/financial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule5QuizPage;

